/**
 * ESG Performance Metrics Component - Modern Redesign
 * 
 * Comprehensive performance metrics dashboard providing:
 * - Financial performance KPIs and analytics
 * - ESG scoring and benchmarking
 * - Risk-adjusted returns analysis
 * - Sustainability impact measurement
 * - Innovation tracking
 * - Stakeholder value analysis
 * 
 * @version 2.0.0
 * @redesigned 2025-10-06
 */

import React, { useState, useEffect } from 'react';
import esgPerformanceMetricsService from '../../../services/esg/esgPerformanceMetricsService';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  PieChart, Pie, Cell, ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip, Legend
} from 'recharts';

const ESGPerformanceMetrics = () => {
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedTimeframe, setSelectedTimeframe] = useState('1Y');

  useEffect(() => {
    loadPerformanceData();
    
    const unsubscribe = esgPerformanceMetricsService.subscribe((data) => {
      setPerformanceData(data);
    });

    return unsubscribe;
  }, []);

  const loadPerformanceData = async () => {
    try {
      setError(null);
      const data = await esgPerformanceMetricsService.getPerformanceMetrics();
      setPerformanceData(data);
    } catch (err) {
      console.error('Error loading performance data:', err);
      setError('Failed to load performance metrics data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      await esgPerformanceMetricsService.refreshData();
    } catch (err) {
      setError('Failed to refresh data. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Loading performance metrics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 text-2xl">⚠</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Data</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={loadPerformanceData}
            className="bg-indigo-600 text-white px-6 py-3 rounded-xl hover:bg-indigo-700 transition-colors font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const renderOverview = () => {
    const kpiCards = [
      { 
        label: 'Total Return', 
        value: '14.2%', 
        change: '+2.3%',
        trend: 'up',
        icon: '📈',
        color: 'from-green-500 to-emerald-600'
      },
      { 
        label: 'ESG Score', 
        value: '85/100', 
        change: '+3 pts',
        trend: 'up',
        icon: '🌱',
        color: 'from-blue-500 to-cyan-600'
      },
      { 
        label: 'Sharpe Ratio', 
        value: '1.42', 
        change: '+0.15',
        trend: 'up',
        icon: '⚖️',
        color: 'from-purple-500 to-indigo-600'
      },
      { 
        label: 'Risk Score', 
        value: '3.2/10', 
        change: '-0.3',
        trend: 'down',
        icon: '🛡️',
        color: 'from-orange-500 to-red-600'
      }
    ];

    const performanceData = [
      { month: 'Jan', financial: 88, esg: 82, combined: 85, benchmark: 80 },
      { month: 'Feb', financial: 90, esg: 84, combined: 87, benchmark: 81 },
      { month: 'Mar', financial: 87, esg: 85, combined: 86, benchmark: 82 },
      { month: 'Apr', financial: 92, esg: 86, combined: 89, benchmark: 83 },
      { month: 'May', financial: 91, esg: 88, combined: 90, benchmark: 84 },
      { month: 'Jun', financial: 93, esg: 87, combined: 90, benchmark: 85 },
      { month: 'Jul', financial: 95, esg: 89, combined: 92, benchmark: 86 },
      { month: 'Aug', financial: 94, esg: 90, combined: 92, benchmark: 87 },
      { month: 'Sep', financial: 96, esg: 91, combined: 94, benchmark: 88 },
      { month: 'Oct', financial: 97, esg: 92, combined: 95, benchmark: 89 }
    ];

    const categoryScores = [
      { category: 'Financial', score: 92, target: 85 },
      { category: 'Environmental', score: 88, target: 80 },
      { category: 'Social', score: 85, target: 82 },
      { category: 'Governance', score: 90, target: 88 },
      { category: 'Innovation', score: 87, target: 85 },
      { category: 'Risk Mgmt', score: 91, target: 87 }
    ];

    const distributionData = [
      { name: 'Excellent', value: 35, color: '#10B981' },
      { name: 'Good', value: 40, color: '#3B82F6' },
      { name: 'Average', value: 20, color: '#F59E0B' },
      { name: 'Needs Work', value: 5, color: '#EF4444' }
    ];

    return (
      <div className="space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {kpiCards.map((kpi, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${kpi.color} flex items-center justify-center text-2xl shadow-lg`}>
                  {kpi.icon}
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  kpi.trend === 'up' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                  {kpi.change}
                </span>
              </div>
              <h3 className="text-gray-600 text-sm font-medium mb-1">{kpi.label}</h3>
              <p className="text-3xl font-bold text-gray-900">{kpi.value}</p>
            </div>
          ))}
        </div>

        {/* Performance Trend Chart */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Performance Trends</h3>
              <p className="text-gray-600 text-sm mt-1">Financial vs ESG vs Combined Performance</p>
            </div>
            <div className="flex gap-2">
              {['1M', '3M', '6M', '1Y', 'YTD', 'ALL'].map((period) => (
                <button
                  key={period}
                  onClick={() => setSelectedTimeframe(period)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    selectedTimeframe === period
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {period}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={performanceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" stroke="#6B7280" />
              <YAxis stroke="#6B7280" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px' }}
              />
              <Legend />
              <Line type="monotone" dataKey="financial" stroke="#3B82F6" strokeWidth={2} name="Financial" />
              <Line type="monotone" dataKey="esg" stroke="#10B981" strokeWidth={2} name="ESG" />
              <Line type="monotone" dataKey="combined" stroke="#8B5CF6" strokeWidth={3} name="Combined" />
              <Line type="monotone" dataKey="benchmark" stroke="#F59E0B" strokeWidth={2} strokeDasharray="5 5" name="Benchmark" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Category Scores & Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Category Performance */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Category Performance vs Targets</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={categoryScores}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="category" stroke="#6B7280" angle={-45} textAnchor="end" height={100} />
                <YAxis stroke="#6B7280" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                />
                <Legend />
                <Bar dataKey="score" fill="#3B82F6" name="Current Score" radius={[8, 8, 0, 0]} />
                <Bar dataKey="target" fill="#E5E7EB" name="Target" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Performance Distribution */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Performance Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={distributionData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {distributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Quick Insights Banner */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl shadow-lg p-6 text-white">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl flex-shrink-0">
              💡
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">Key Insights</h3>
              <ul className="space-y-1 text-white/90">
                <li>• Overall performance up 14.2% YTD, exceeding benchmark by 6.2%</li>
                <li>• ESG score improved by 3 points to 85/100, ranking in top quartile</li>
                <li>• Risk-adjusted returns (Sharpe 1.42) indicate strong performance efficiency</li>
                <li>• All category scores above targets, with Financial leading at 92/100</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFinancial = () => {
    const financialMetrics = [
      { metric: 'Revenue Growth', value: '18.5%', target: '15%', status: 'excellent' },
      { metric: 'EBITDA Margin', value: '24.2%', target: '22%', status: 'excellent' },
      { metric: 'Net Profit Margin', value: '16.8%', target: '15%', status: 'good' },
      { metric: 'Return on Equity', value: '22.4%', target: '20%', status: 'excellent' },
      { metric: 'Return on Assets', value: '14.6%', target: '12%', status: 'excellent' },
      { metric: 'Free Cash Flow', value: '$2.4B', target: '$2.0B', status: 'excellent' }
    ];

    const revenueData = [
      { quarter: 'Q1 23', revenue: 2.2, profit: 0.35, margin: 16 },
      { quarter: 'Q2 23', revenue: 2.4, profit: 0.38, margin: 16.5 },
      { quarter: 'Q3 23', revenue: 2.6, profit: 0.42, margin: 16.8 },
      { quarter: 'Q4 23', revenue: 2.8, profit: 0.45, margin: 17 },
      { quarter: 'Q1 24', revenue: 3.0, profit: 0.48, margin: 16.5 },
      { quarter: 'Q2 24', revenue: 3.2, profit: 0.52, margin: 16.8 },
      { quarter: 'Q3 24', revenue: 3.4, profit: 0.56, margin: 17.2 }
    ];

    const getStatusColor = (status) => {
      switch (status) {
        case 'excellent': return 'bg-green-100 text-green-700 border-green-200';
        case 'good': return 'bg-blue-100 text-blue-700 border-blue-200';
        case 'average': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
        default: return 'bg-gray-100 text-gray-700 border-gray-200';
      }
    };

    return (
      <div className="space-y-6">
        {/* Financial Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {financialMetrics.map((item, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-gray-600 font-medium">{item.metric}</h3>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(item.status)}`}>
                  {item.status}
                </span>
              </div>
              <div className="flex items-baseline gap-3">
                <p className="text-3xl font-bold text-gray-900">{item.value}</p>
                <p className="text-sm text-gray-500">Target: {item.target}</p>
              </div>
              <div className="mt-4 bg-gray-100 rounded-full h-2 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-500 to-indigo-600 h-full rounded-full" style={{ width: '85%' }}></div>
              </div>
            </div>
          ))}
        </div>

        {/* Revenue & Profitability Chart */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Revenue & Profitability Trends</h3>
          <ResponsiveContainer width="100%" height={350}>
            <AreaChart data={revenueData}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="quarter" stroke="#6B7280" />
              <YAxis stroke="#6B7280" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px' }}
              />
              <Legend />
              <Area type="monotone" dataKey="revenue" stroke="#3B82F6" fillOpacity={1} fill="url(#colorRevenue)" name="Revenue ($B)" />
              <Area type="monotone" dataKey="profit" stroke="#10B981" fillOpacity={1} fill="url(#colorProfit)" name="Profit ($B)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Financial Health Score */}
        <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl shadow-lg p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold mb-2">Financial Health Score</h3>
              <p className="text-blue-100 mb-4">Comprehensive assessment across all key metrics</p>
              <div className="flex gap-8">
                <div>
                  <p className="text-sm text-blue-200">Liquidity</p>
                  <p className="text-3xl font-bold">92/100</p>
                </div>
                <div>
                  <p className="text-sm text-blue-200">Profitability</p>
                  <p className="text-3xl font-bold">88/100</p>
                </div>
                <div>
                  <p className="text-sm text-blue-200">Efficiency</p>
                  <p className="text-3xl font-bold">85/100</p>
                </div>
              </div>
            </div>
            <div className="text-center">
              <div className="w-32 h-32 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <div>
                  <p className="text-5xl font-bold">A+</p>
                  <p className="text-sm text-blue-200">Grade</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderESG = () => {
    const esgScores = [
      { category: 'Carbon Emissions', current: 88, target: 85, industry: 75 },
      { category: 'Water Usage', current: 82, target: 80, industry: 72 },
      { category: 'Waste Management', current: 90, target: 85, industry: 78 },
      { category: 'Energy Efficiency', current: 85, target: 82, industry: 70 },
      { category: 'Renewable Energy', current: 78, target: 75, industry: 65 },
      { category: 'Biodiversity', current: 75, target: 70, industry: 68 }
    ];

    const radarData = [
      { subject: 'Environmental', score: 88, industry: 75, fullMark: 100 },
      { subject: 'Social', score: 85, industry: 78, fullMark: 100 },
      { subject: 'Governance', score: 90, industry: 82, fullMark: 100 },
      { subject: 'Ethics', score: 92, industry: 80, fullMark: 100 },
      { subject: 'Transparency', score: 87, industry: 76, fullMark: 100 },
      { subject: 'Compliance', score: 94, industry: 85, fullMark: 100 }
    ];

    const impactData = [
      { month: 'Jan', carbon: 85, water: 80, waste: 88 },
      { month: 'Feb', carbon: 86, water: 82, waste: 89 },
      { month: 'Mar', carbon: 87, water: 83, waste: 90 },
      { month: 'Apr', carbon: 88, water: 84, waste: 90 },
      { month: 'May', carbon: 89, water: 85, waste: 91 },
      { month: 'Jun', carbon: 90, water: 86, waste: 92 }
    ];

    return (
      <div className="space-y-6">
        {/* ESG Category Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl shadow-lg p-6 text-white">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl">
                🌍
              </div>
              <div>
                <h3 className="text-lg font-semibold">Environmental</h3>
                <p className="text-sm text-green-100">Climate & Resources</p>
              </div>
            </div>
            <p className="text-4xl font-bold mb-2">88/100</p>
            <p className="text-sm text-green-100">↑ +3 from last quarter</p>
          </div>

          <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl shadow-lg p-6 text-white">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl">
                👥
              </div>
              <div>
                <h3 className="text-lg font-semibold">Social</h3>
                <p className="text-sm text-blue-100">People & Community</p>
              </div>
            </div>
            <p className="text-4xl font-bold mb-2">85/100</p>
            <p className="text-sm text-blue-100">↑ +2 from last quarter</p>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl shadow-lg p-6 text-white">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl">
                ⚖️
              </div>
              <div>
                <h3 className="text-lg font-semibold">Governance</h3>
                <p className="text-sm text-purple-100">Ethics & Leadership</p>
              </div>
            </div>
            <p className="text-4xl font-bold mb-2">90/100</p>
            <p className="text-sm text-purple-100">↑ +4 from last quarter</p>
          </div>
        </div>

        {/* Radar Chart & Impact Trends */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ESG Radar Comparison */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">ESG Performance Comparison</h3>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#E5E7EB" />
                <PolarAngleAxis dataKey="subject" stroke="#6B7280" />
                <PolarRadiusAxis stroke="#6B7280" />
                <Radar name="Our Score" dataKey="score" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.6} />
                <Radar name="Industry Avg" dataKey="industry" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.3} />
                <Legend />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          {/* Environmental Impact Trends */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Environmental Impact Trends</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={impactData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" stroke="#6B7280" />
                <YAxis stroke="#6B7280" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                />
                <Legend />
                <Line type="monotone" dataKey="carbon" stroke="#10B981" strokeWidth={2} name="Carbon Reduction" />
                <Line type="monotone" dataKey="water" stroke="#3B82F6" strokeWidth={2} name="Water Efficiency" />
                <Line type="monotone" dataKey="waste" stroke="#8B5CF6" strokeWidth={2} name="Waste Management" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Detailed ESG Metrics */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Detailed ESG Performance Metrics</h3>
          <div className="space-y-4">
            {esgScores.map((item, index) => (
              <div key={index} className="border border-gray-200 rounded-xl p-4 hover:border-indigo-300 transition-colors">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-900">{item.category}</h4>
                  <div className="flex gap-4 text-sm">
                    <span className="text-gray-600">Current: <strong className="text-indigo-600">{item.current}</strong></span>
                    <span className="text-gray-600">Target: <strong>{item.target}</strong></span>
                    <span className="text-gray-600">Industry: <strong>{item.industry}</strong></span>
                  </div>
                </div>
                <div className="relative">
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-400 to-green-600 rounded-full transition-all duration-500"
                      style={{ width: `${item.current}%` }}
                    ></div>
                  </div>
                  <div 
                    className="absolute top-0 w-0.5 h-3 bg-orange-500"
                    style={{ left: `${item.target}%` }}
                    title="Target"
                  ></div>
                  <div 
                    className="absolute top-0 w-0.5 h-3 bg-gray-400"
                    style={{ left: `${item.industry}%` }}
                    title="Industry Average"
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderInnovation = () => {
    const innovationMetrics = [
      { label: 'R&D Investment', value: '$850M', change: '+12%', icon: '🔬' },
      { label: 'Patents Filed', value: '142', change: '+18', icon: '📝' },
      { label: 'Innovation Score', value: '87/100', change: '+5', icon: '💡' },
      { label: 'Time to Market', value: '8.2 months', change: '-1.3', icon: '⚡' }
    ];

    const projectData = [
      { name: 'AI Integration', stage: 'Production', progress: 95, investment: '$120M', impact: 'High' },
      { name: 'Green Tech R&D', stage: 'Development', progress: 75, investment: '$85M', impact: 'High' },
      { name: 'Digital Platform', stage: 'Testing', progress: 60, investment: '$95M', impact: 'Medium' },
      { name: 'Automation Suite', stage: 'Planning', progress: 30, investment: '$70M', impact: 'Medium' },
      { name: 'Quantum Computing', stage: 'Research', progress: 15, investment: '$150M', impact: 'High' }
    ];

    const investmentData = [
      { category: 'AI/ML', amount: 250, percentage: 29 },
      { category: 'Sustainability', amount: 180, percentage: 21 },
      { category: 'Digital Transform', amount: 160, percentage: 19 },
      { category: 'Automation', amount: 140, percentage: 16 },
      { category: 'Research', amount: 120, percentage: 15 }
    ];

    const getStageColor = (stage) => {
      const colors = {
        'Production': 'bg-green-100 text-green-700',
        'Development': 'bg-blue-100 text-blue-700',
        'Testing': 'bg-yellow-100 text-yellow-700',
        'Planning': 'bg-purple-100 text-purple-700',
        'Research': 'bg-orange-100 text-orange-700'
      };
      return colors[stage] || 'bg-gray-100 text-gray-700';
    };

    return (
      <div className="space-y-6">
        {/* Innovation KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {innovationMetrics.map((metric, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center text-xl">
                  {metric.icon}
                </div>
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded-lg text-xs font-semibold">
                  {metric.change}
                </span>
              </div>
              <h3 className="text-gray-600 text-sm font-medium mb-1">{metric.label}</h3>
              <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
            </div>
          ))}
        </div>

        {/* Innovation Projects */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Active Innovation Projects</h3>
          <div className="space-y-4">
            {projectData.map((project, index) => (
              <div key={index} className="border border-gray-200 rounded-xl p-4 hover:border-indigo-300 transition-colors">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <h4 className="font-semibold text-gray-900">{project.name}</h4>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStageColor(project.stage)}`}>
                      {project.stage}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-600">Investment: <strong>{project.investment}</strong></span>
                    <span className="text-gray-600">Impact: <strong className="text-indigo-600">{project.impact}</strong></span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full transition-all duration-500"
                      style={{ width: `${project.progress}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-semibold text-gray-700 w-12 text-right">{project.progress}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* R&D Investment Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">R&D Investment Distribution</h3>
            <div className="space-y-4">
              {investmentData.map((item, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-700">{item.category}</span>
                    <span className="text-sm text-gray-600">${item.amount}M ({item.percentage}%)</span>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full"
                      style={{ width: `${item.percentage * 3.33}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Innovation Impact Summary */}
          <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl shadow-lg p-6 text-white">
            <h3 className="text-xl font-bold mb-6">Innovation Impact Summary</h3>
            <div className="space-y-6">
              <div>
                <p className="text-purple-200 text-sm mb-2">Technology Adoption Rate</p>
                <p className="text-4xl font-bold">78%</p>
                <div className="mt-2 h-2 bg-white/20 rounded-full overflow-hidden">
                  <div className="h-full bg-white rounded-full" style={{ width: '78%' }}></div>
                </div>
              </div>
              <div>
                <p className="text-purple-200 text-sm mb-2">Innovation ROI</p>
                <p className="text-4xl font-bold">245%</p>
                <div className="mt-2 h-2 bg-white/20 rounded-full overflow-hidden">
                  <div className="h-full bg-white rounded-full" style={{ width: '85%' }}></div>
                </div>
              </div>
              <div>
                <p className="text-purple-200 text-sm mb-2">Market Competitiveness</p>
                <p className="text-4xl font-bold">92/100</p>
                <div className="mt-2 h-2 bg-white/20 rounded-full overflow-hidden">
                  <div className="h-full bg-white rounded-full" style={{ width: '92%' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStakeholders = () => {
    const stakeholderGroups = [
      { group: 'Investors', satisfaction: 88, engagement: 92, value: '$2.4B', trend: 'up' },
      { group: 'Employees', satisfaction: 85, engagement: 89, value: '12,500', trend: 'up' },
      { group: 'Customers', satisfaction: 90, engagement: 87, value: '2.8M', trend: 'up' },
      { group: 'Suppliers', satisfaction: 82, engagement: 85, value: '850', trend: 'stable' },
      { group: 'Community', satisfaction: 78, engagement: 80, value: '45 cities', trend: 'up' },
      { group: 'Regulators', satisfaction: 92, engagement: 95, value: '100%', trend: 'up' }
    ];

    const satisfactionData = [
      { month: 'Jan', investors: 85, employees: 82, customers: 88, community: 75 },
      { month: 'Feb', investors: 86, employees: 83, customers: 89, community: 76 },
      { month: 'Mar', investors: 87, employees: 84, customers: 89, community: 77 },
      { month: 'Apr', investors: 87, employees: 85, customers: 90, community: 77 },
      { month: 'May', investors: 88, employees: 85, customers: 90, community: 78 },
      { month: 'Jun', investors: 88, employees: 85, customers: 90, community: 78 }
    ];

    const valueCreation = [
      { category: 'Economic Value', amount: 2400, color: '#3B82F6' },
      { category: 'Social Value', amount: 850, color: '#10B981' },
      { category: 'Environmental Value', amount: 620, color: '#8B5CF6' },
      { category: 'Governance Value', amount: 480, color: '#F59E0B' }
    ];

    return (
      <div className="space-y-6">
        {/* Stakeholder Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {stakeholderGroups.map((group, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">{group.group}</h3>
                <span className={`text-xl ${group.trend === 'up' ? 'text-green-500' : 'text-gray-400'}`}>
                  {group.trend === 'up' ? '↗' : '→'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-xs text-gray-600 mb-1">Satisfaction</p>
                  <p className="text-2xl font-bold text-indigo-600">{group.satisfaction}%</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Engagement</p>
                  <p className="text-2xl font-bold text-purple-600">{group.engagement}%</p>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">Value Created</p>
                <p className="text-xl font-bold text-gray-900">{group.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Satisfaction Trends */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Stakeholder Satisfaction Trends</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={satisfactionData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" stroke="#6B7280" />
              <YAxis stroke="#6B7280" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px' }}
              />
              <Legend />
              <Line type="monotone" dataKey="investors" stroke="#3B82F6" strokeWidth={2} name="Investors" />
              <Line type="monotone" dataKey="employees" stroke="#10B981" strokeWidth={2} name="Employees" />
              <Line type="monotone" dataKey="customers" stroke="#8B5CF6" strokeWidth={2} name="Customers" />
              <Line type="monotone" dataKey="community" stroke="#F59E0B" strokeWidth={2} name="Community" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Value Creation Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Stakeholder Value Creation</h3>
            <div className="space-y-4">
              {valueCreation.map((item, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-700">{item.category}</span>
                    <span className="text-sm font-bold text-gray-900">${item.amount}M</span>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${(item.amount / 2400) * 100}%`, backgroundColor: item.color }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Engagement Insights */}
          <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl shadow-lg p-6 text-white">
            <h3 className="text-xl font-bold mb-6">Engagement Insights</h3>
            <div className="space-y-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <p className="text-sm text-indigo-100 mb-1">Overall Engagement Score</p>
                <p className="text-3xl font-bold">88.3%</p>
                <p className="text-xs text-indigo-200 mt-1">↑ +4.2% from last quarter</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <p className="text-sm text-indigo-100 mb-1">Total Stakeholders</p>
                <p className="text-3xl font-bold">3.2M+</p>
                <p className="text-xs text-indigo-200 mt-1">Across all groups</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <p className="text-sm text-indigo-100 mb-1">Value Delivered</p>
                <p className="text-3xl font-bold">$4.35B</p>
                <p className="text-xs text-indigo-200 mt-1">Total stakeholder value</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAnalytics = () => {
    const predictiveMetrics = [
      { metric: 'Next Quarter Revenue', prediction: '$3.6B', confidence: '92%', trend: 'up' },
      { metric: 'ESG Score Projection', prediction: '87/100', confidence: '88%', trend: 'up' },
      { metric: 'Risk Assessment', prediction: 'Low-Medium', confidence: '85%', trend: 'stable' },
      { metric: 'Market Position', prediction: 'Top 10%', confidence: '90%', trend: 'up' }
    ];

    const benchmarkData = [
      { metric: 'Financial Performance', our: 92, industry: 78, topQuartile: 88 },
      { metric: 'ESG Score', our: 88, industry: 72, topQuartile: 85 },
      { metric: 'Innovation Index', our: 87, industry: 70, topQuartile: 82 },
      { metric: 'Risk Management', our: 91, industry: 75, topQuartile: 86 },
      { metric: 'Stakeholder Value', our: 89, industry: 73, topQuartile: 84 }
    ];

    const forecastData = [
      { period: 'Q1', actual: 88, forecast: 88, confidence: 95 },
      { period: 'Q2', actual: 90, forecast: 89, confidence: 93 },
      { period: 'Q3', actual: 92, forecast: 91, confidence: 90 },
      { period: 'Q4', actual: 94, forecast: 93, confidence: 88 },
      { period: 'Q1 Next', actual: null, forecast: 95, confidence: 85 },
      { period: 'Q2 Next', actual: null, forecast: 97, confidence: 80 },
      { period: 'Q3 Next', actual: null, forecast: 98, confidence: 75 }
    ];

    return (
      <div className="space-y-6">
        {/* Predictive Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {predictiveMetrics.map((item, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-600">{item.metric}</h3>
                <span className={`text-xl ${item.trend === 'up' ? 'text-green-500' : 'text-gray-400'}`}>
                  {item.trend === 'up' ? '↗' : '→'}
                </span>
              </div>
              <p className="text-2xl font-bold text-gray-900 mb-2">{item.prediction}</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full"
                    style={{ width: item.confidence }}
                  ></div>
                </div>
                <span className="text-xs font-semibold text-indigo-600">{item.confidence}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Benchmark Comparison */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Industry Benchmark Comparison</h3>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={benchmarkData} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis type="number" stroke="#6B7280" />
              <YAxis dataKey="metric" type="category" stroke="#6B7280" width={150} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px' }}
              />
              <Legend />
              <Bar dataKey="our" fill="#3B82F6" name="Our Performance" radius={[0, 8, 8, 0]} />
              <Bar dataKey="topQuartile" fill="#8B5CF6" name="Top Quartile" radius={[0, 8, 8, 0]} />
              <Bar dataKey="industry" fill="#E5E7EB" name="Industry Average" radius={[0, 8, 8, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Performance Forecast */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Performance Forecast with Confidence Intervals</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={forecastData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="period" stroke="#6B7280" />
              <YAxis stroke="#6B7280" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px' }}
              />
              <Legend />
              <Line type="monotone" dataKey="actual" stroke="#3B82F6" strokeWidth={3} name="Actual" dot={{ r: 5 }} />
              <Line type="monotone" dataKey="forecast" stroke="#8B5CF6" strokeWidth={2} strokeDasharray="5 5" name="Forecast" />
              <Line type="monotone" dataKey="confidence" stroke="#10B981" strokeWidth={1} name="Confidence %" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Analytics Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl shadow-lg p-6 text-white">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl mb-4">
              📊
            </div>
            <h3 className="text-lg font-semibold mb-2">Predictive Accuracy</h3>
            <p className="text-4xl font-bold mb-2">92.5%</p>
            <p className="text-sm text-blue-100">Average across all models</p>
          </div>

          <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl shadow-lg p-6 text-white">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl mb-4">
              🎯
            </div>
            <h3 className="text-lg font-semibold mb-2">Benchmark Position</h3>
            <p className="text-4xl font-bold mb-2">Top 8%</p>
            <p className="text-sm text-purple-100">Industry ranking position</p>
          </div>

          <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl shadow-lg p-6 text-white">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl mb-4">
              ⚡
            </div>
            <h3 className="text-lg font-semibold mb-2">Growth Trajectory</h3>
            <p className="text-4xl font-bold mb-2">+18.5%</p>
            <p className="text-sm text-indigo-100">Projected annual growth</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white shadow-2xl">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2">Performance Metrics Dashboard</h1>
              <p className="text-blue-100 text-lg">Comprehensive analytics across financial, ESG, and innovation dimensions</p>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <p className="text-sm text-blue-200">Overall Score</p>
                <p className="text-3xl font-bold">92/100</p>
              </div>
              <button 
                onClick={handleRefresh}
                className="bg-white/20 backdrop-blur-sm hover:bg-white/30 px-6 py-3 rounded-xl font-medium transition-colors flex items-center gap-2"
              >
                <span>🔄</span> Refresh
              </button>
            </div>
          </div>

          {/* Timeframe Selector */}
          <div className="flex gap-2 mt-6">
            {['1M', '3M', '6M', '1Y', 'YTD', 'ALL'].map((period) => (
              <button
                key={period}
                onClick={() => setSelectedTimeframe(period)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedTimeframe === period
                    ? 'bg-white text-indigo-600 shadow-lg'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {period}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1">
            {[
              { id: 'overview', label: 'Overview', icon: '📊' },
              { id: 'financial', label: 'Financial', icon: '💰' },
              { id: 'esg', label: 'ESG', icon: '🌱' },
              { id: 'innovation', label: 'Innovation', icon: '💡' },
              { id: 'stakeholders', label: 'Stakeholders', icon: '👥' },
              { id: 'analytics', label: 'Analytics', icon: '📈' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-all border-b-2 ${
                  activeTab === tab.id
                    ? 'text-indigo-600 border-indigo-600 bg-indigo-50'
                    : 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <span className="text-xl">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'financial' && renderFinancial()}
        {activeTab === 'esg' && renderESG()}
        {activeTab === 'innovation' && renderInnovation()}
        {activeTab === 'stakeholders' && renderStakeholders()}
        {activeTab === 'analytics' && renderAnalytics()}
      </div>
    </div>
  );
};

export default ESGPerformanceMetrics;
